/** Provider-independent, browser-safe shot material contract. */
export const ENTITY_ROLES=Object.freeze(['identity','location','prop','prop-start','prop-end','costume','style','lighting']);
export function resolveShotComposition(shot={},purpose='working') {
  const versions=(shot.compositionVersions||[]).filter(item=>!item.staleAt&&item.status!=='stale'&&!(item.source==='previs'&&Number(shot.duration)&&Number(item.sceneGraph?.duration)&&Math.abs(Number(shot.duration)-Number(item.sceneGraph.duration))>0.25));
  if(purpose==='video')return versions.find(item=>item.id===shot.lockedCompositionId);
  return versions.find(item=>item.id===shot.workingCompositionId)||versions.find(item=>item.id===shot.lockedCompositionId)||versions.find(item=>item.id===shot.approvedCompositionId)||versions.slice().reverse().find(item=>item.mediaId||item.url);
}
export function projectAspectRatio(project={}) {
  return [project.format,project.aspectRatio,project.camera?.aspectRatio].map(value=>String(value||'').match(/\d+(?:\.\d+)?\s*:\s*\d+(?:\.\d+)?/)?.[0].replace(/\s/g,'')).find(Boolean)||'';
}
export function normalizeTransition(value) {
  if(typeof value==='string')return value.trim()||'cut';
  if(!value||typeof value!=='object')return 'cut';
  return [value.type||value.kind||value.name||'cut',value.to||value.target,value.description||value.reason,Number.isFinite(Number(value.duration))&&value.duration!==''?`${Number(value.duration)}s`:null].filter(Boolean).join(' — ');
}
export function describeValue(value) {
  if(value==null)return '';
  if(typeof value!=='object')return String(value);
  if(Array.isArray(value))return value.map(describeValue).filter(Boolean).join('; ');
  return Object.entries(value).filter(([key,v])=>!['id','entityId','variantId','shotId','axisId'].includes(key)&&v!=null&&v!=='')
    .map(([key,v])=>`${key.replace(/([a-z])([A-Z])/g,'$1 $2')}: ${describeValue(v)}`).join('; ');
}
export function resolveAssetMedia(asset, variant = null) {
  const read=record=>{
    const mediaId=record?.mediaId||record?.image?.mediaId||record?.identitySheet?.masterMediaId||null;
    return {mediaId,url:mediaId?`/api/media/${encodeURIComponent(mediaId)}`:record?.imageUrl||record?.url||record?.image?.url||(typeof record?.image==='string'?record.image:null)||record?.identitySheet?.masterImageUrl||null};
  };
  if(!variant)return read(asset);
  const own=read(variant);
  if(own.mediaId||own.url)return own;
  return variant.inheritIdentity===true?read(asset):own;
}
export function selectAssetMedia(asset, media) {
  const url=media.id?`/api/media/${encodeURIComponent(media.id)}`:media.url;
  Object.assign(asset,{mediaId:media.id||null,imageUrl:url,mediaUrl:url});
  asset.identitySheet={...asset.identitySheet,masterMediaId:media.id||null,masterImageUrl:url};
  asset.generations=(asset.generations||[]).map(item=>({...item,isAnchor:media.id?item.mediaId===media.id:(item.url||item.imageUrl)===url}));
}
export function flagAssetReferenceChange(project, asset, previousMediaId, at=new Date().toISOString()) {
  if(!previousMediaId||previousMediaId===asset.mediaId)return;
  for(const shot of project.shots||[]){
    if(!(shot.entityTags||[]).some(tag=>tag.entityId===asset.id))continue;
    if(!(shot.compositionVersions?.length||shot.takes?.length))continue;
    shot.referenceChanges=[...(shot.referenceChanges||[]).filter(item=>item.assetId!==asset.id),{assetId:asset.id,name:asset.name,previousMediaId,mediaId:asset.mediaId,at}];
  }
}
export function needsTemporalReference(tag={}, tags=[]) {
  if(tag.referenceRequired===true||tag.visibleAtStart===false||tag.role==='prop-end')return true;
  const same=tags.filter(t=>t.entityId===tag.entityId);
  return same.some(t=>/\b(conceal(?:ed)?|hidden|inside.*(?:fist|pocket)|not visible)\b/i.test(t.state||''))
    && same.some(t=>/\b(reveal|emerg(?:e|es|ing)|visible|open(?:s|ing)?)\b/i.test(t.state||''));
}
export function temporalReferenceFindings(shot, selected=[]) {
  const findings=[];
  for(const tag of shot.entityTags||[]){
    if(!needsTemporalReference(tag,shot.entityTags))continue;
    const laterState=(shot.entityTags||[]).some(t=>t.entityId===tag.entityId&&t.role==='prop-end');
    if(tag.role==='prop-start'&&laterState&&!tag.referenceRequired)continue;
    const covered=selected.some(r=> (r.bindings||[r]).some(b=>b.entityId===tag.entityId&&(!tag.variantId||b.variantId===tag.variantId)
      && (r.type==='image-reference'||r.type==='element-reference'||(r.type==='last-frame'&&r.locked===true))));
    if(!covered)findings.push({code:'unseen-asset-reference',severity:'blocker',entityId:tag.entityId,
      message:`${shot.code||shot.id}: a hidden or later-revealed asset/state (${tag.entityId}${tag.variantId?` / ${tag.variantId}`:''}) has no compatible visual reference. Bind a supported reference, approve an end frame, or direct a separate reveal insert.`});
  }
  return findings;
}
